import { useState } from 'react';
import { Activity, Bot, Code, Stethoscope, ArrowRight, Clock, ExternalLink, X, Bell, Zap, Shield, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Products() {
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const products = [
    {
      id: 'ai-med-analytics',
      name: 'AI Med Analytics',
      icon: Activity,
      tagline: 'Predictive Cardiac Health Analytics',
      description: 'Advanced cardiovascular analysis powered by deep learning algorithms. Get instant insights into cardiac health with industry-leading accuracy.',
      fullDescription: 'Advanced machine learning algorithms that analyze cardiac data to predict cardiovascular events before they occur. FDA-registered and clinically validated with 99.7% accuracy.',
      features: ['Real-time ECG analysis', 'Predictive risk assessment', 'Integration with existing EMR systems', 'HIPAA-compliant data handling', 'Continuous monitoring'],
      status: 'available',
      link: 'https://cardiolyzer.com',
      color: 'from-cyan-500 to-blue-600',
      launch: ''
    },
    {
      id: 'smartmed-ai',
      name: 'SmartMed AI',
      icon: Stethoscope,
      tagline: 'Intelligent Clinical Decision Support',
      description: 'AI-powered clinical decision support system that assists physicians in diagnosis and treatment planning with evidence-based recommendations.',
      fullDescription: 'An AI-powered clinical decision support system that provides evidence-based recommendations to healthcare providers in real-time, improving diagnostic accuracy and treatment outcomes.',
      features: ['Evidence-Based Diagnosis', 'Treatment Recommendations', 'Drug Interaction Alerts', 'Clinical Guidelines Integration', 'EHR Integration'],
      status: 'coming-soon',
      color: 'from-emerald-500 to-teal-600',
      launch: 'Q2 2026'
    },
    {
      id: 'medibot-ai',
      name: 'MediBot AI',
      icon: Bot,
      tagline: 'AI-Powered Patient Communication',
      description: 'AI-powered virtual assistant for patient engagement, appointment scheduling, and preliminary symptom assessment available 24/7.',
      fullDescription: 'Intelligent conversational AI that handles patient inquiries, appointment scheduling, follow-up care, and triage with human-like understanding and empathy—available 24/7.',
      features: ['24/7 Patient Support', 'Appointment Scheduling', 'Symptom Assessment', 'Multi-Language Support', 'HIPAA Compliant'],
      status: 'coming-soon',
      color: 'from-purple-500 to-indigo-600',
      launch: 'Q3 2026'
    },
    {
      id: 'ai-medical-coder',
      name: 'AI Medical Coder',
      icon: Code,
      tagline: 'Automated Medical Coding & Billing',
      description: 'Automated medical coding solution that ensures accurate ICD-10 and CPT code assignment, reducing claim denials and improving revenue cycle.',
      fullDescription: 'Streamline your revenue cycle management with AI that automatically assigns accurate ICD-10, CPT, and HCPCS codes—reducing errors, accelerating reimbursements, and ensuring compliance.',
      features: ['ICD-10 Auto-Coding', 'CPT Code Suggestions', 'Compliance Verification', 'Revenue Optimization', 'Audit Trail'],
      status: 'coming-soon',
      color: 'from-orange-500 to-red-600',
      launch: 'Q4 2026'
    }
  ];

  const handleNotify = () => {
    if (email && email.includes('@')) {
      setSubmitted(true);
    }
  };

  const closeModal = () => {
    setSelectedProduct(null);
    setEmail('');
    setSubmitted(false);
  };

  const selected = products.find(p => p.id === selectedProduct);

  return (
    <div className="min-h-screen bg-[#030712]">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[100px]" />
      </div>

      {/* Hero Section */}
      <section className="relative z-10 pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full mb-6">
            <Zap className="w-4 h-4 text-purple-400" />
            <span className="text-purple-400 text-sm font-medium">Our Products</span>
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">
            AI-Powered Healthcare{' '}
            <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Solutions
            </span>
          </h1>
          <p className="text-xl text-gray-400 leading-relaxed">
            Comprehensive suite of artificial intelligence products designed to transform
            every aspect of healthcare delivery and management.
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {products.map((product) => (
              <div key={product.id} className="group relative">
                <div className={`absolute inset-0 bg-gradient-to-br ${product.color} rounded-3xl opacity-0 group-hover:opacity-10 blur-xl transition-opacity duration-500`} />
                <div className="relative h-full bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/10 rounded-3xl overflow-hidden hover:border-white/20 transition-all">
                  {/* Header */}
                  <div className={`bg-gradient-to-br ${product.color} p-8`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl group-hover:scale-110 transition-transform">
                        <product.icon className="w-8 h-8 text-white" />
                      </div>
                      {product.status === 'coming-soon' && (
                        <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium text-white flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          Coming Soon
                        </span>
                      )}
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">{product.name}</h3>
                    <p className="text-white/90 leading-relaxed">{product.description}</p>
                  </div>

                  {/* Body */}
                  <div className="p-8">
                    <h4 className="text-sm font-semibold text-gray-400 mb-4 uppercase tracking-wide">Key Features</h4>
                    <ul className="space-y-3 mb-8">
                      {product.features.slice(0, 4).map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-400">
                          <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-cyan-400 flex-shrink-0" />
                          <span className="text-sm leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {product.status === 'available' ? (
                      <a
                        href={product.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center justify-center w-full px-6 py-4 bg-gradient-to-r ${product.color} text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-cyan-500/25 transition-all`}
                      >
                        Visit Cardiolyzer.com
                        <ExternalLink className="ml-2 w-5 h-5" />
                      </a>
                    ) : (
                      <button
                        onClick={() => setSelectedProduct(product.id)}
                        className="inline-flex items-center justify-center w-full px-6 py-4 bg-white/5 border border-white/10 text-white font-semibold rounded-xl hover:bg-white/10 transition-all"
                      >
                        Learn More
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 via-blue-600 to-purple-600" />
            <div className="relative px-8 py-16 lg:px-16 lg:py-20 text-center">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Interested in Our Products?
              </h2>
              <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
                Get in touch to learn more about how PulseNex can transform your healthcare practice.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 bg-white text-gray-900 rounded-xl font-semibold hover:bg-gray-100 transition-all"
              >
                Contact Sales
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Coming Soon Modal */}
      {selectedProduct && selected && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={closeModal}>
          <div 
            className="bg-[#030712] border border-white/10 rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative p-8">
              {/* Close Button */}
              <button
                onClick={closeModal}
                className="absolute top-6 right-6 w-10 h-10 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>

              {/* Content */}
              <div className="text-center">
                <div className={`w-20 h-20 rounded-3xl bg-gradient-to-br ${selected.color} p-5 mx-auto mb-6`}>
                  <selected.icon className="w-full h-full text-white" />
                </div>

                <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/20 rounded-full mb-6">
                  <Clock className="w-4 h-4 text-yellow-400" />
                  <span className="text-yellow-400 text-sm font-medium">Coming {selected.launch}</span>
                </div>

                <h2 className="text-3xl font-bold text-white mb-2">{selected.name}</h2>
                <p className={`text-lg font-medium mb-4 ${
                  selected.color.includes('emerald') ? 'text-emerald-400' :
                  selected.color.includes('purple') ? 'text-purple-400' :
                  'text-orange-400'
                }`}>{selected.tagline}</p>
                <p className="text-gray-400 leading-relaxed mb-8">{selected.fullDescription}</p>

                {/* Features */}
                <div className="flex flex-wrap justify-center gap-2 mb-8">
                  {selected.features.map((feature, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-sm text-gray-300"
                    >
                      <Zap className="w-3 h-3 text-cyan-400" />
                      {feature}
                    </span>
                  ))}
                </div>

                {/* Notify Form */}
                {!submitted ? (
                  <div className="mt-8">
                    <p className="flex items-center justify-center gap-2 text-gray-400 text-sm mb-4">
                      <Bell className="w-4 h-4" />
                      Be the first to know when we launch
                    </p>
                    <div className="flex gap-3">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 transition-colors"
                      />
                      <button
                        onClick={handleNotify}
                        className={`px-6 py-3 bg-gradient-to-r ${selected.color} rounded-xl font-semibold text-white hover:opacity-90 transition-opacity`}
                      >
                        Notify Me
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-8 p-6 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl">
                    <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="w-6 h-6 text-emerald-400" />
                    </div>
                    <h4 className="text-lg font-semibold text-white mb-2">You're on the list!</h4>
                    <p className="text-gray-400 text-sm">We'll notify you at {email} when {selected.name} launches.</p>
                  </div>
                )}

                {/* Trust Badges */}
                <div className="mt-8 pt-8 border-t border-white/10 flex items-center justify-center gap-6 text-gray-500 text-sm">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    <span>HIPAA Compliant</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    <span>Enterprise Ready</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
